import { Injectable } from '@angular/core';
import { BasicChannel, EncChannel, EncField, Encoding, MarkType } from '@mode-vegas/vegas';
import { EncodingHelpers } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledFormula } from '@mode/shared/util-switch';
import { FieldDropHandlerService } from '../cartesian-builder-common/field-drop-handler.service';

@Injectable({
  providedIn: 'root',
})
export class ComboDropHandlerService extends FieldDropHandlerService {
  /**
   * For dual axis, the primary fields are spread across the y and y2.
   * Bar-Line currently doesn't support a horizontal option so we don't
   * need to look at x and x2.
   */
  getPrimaryFields(encoding: Encoding) {
    return [...encoding.y, ...encoding.y2];
  }

  /**
   * The channel is always 'row' since there is no horizontal option
   */
  getPrimaryChannel(): 'row' | 'column' | 'value' {
    return 'row';
  }

  handleDropOnExistingField(
    encoding: Encoding,
    field: EncField,
    formula: CompiledFormula,
    channel: BasicChannel,
    source: EncChannel | 'field-list' | undefined,
    useMuzeRenderer: boolean,
    zone?: EncodingHelpers.ZoneType
  ) {
    let result = this.replaceFieldWithFormula(encoding, field, formula, channel, source, zone);

    if (channel === 'row' && result != null) {
      // If we are replacing a field on row, we need to make sure that we keep the
      // dual axis in place.
      result = this.ensureDualAxis(result.encoding);
    }
    return result;
  }

  handleDropOnEmpty(
    view: FlamingoTypes.ModeView | undefined,
    encoding: Encoding,
    chartType: ChartTypes.ChartType,
    formula: CompiledFormula,
    channel: BasicChannel,
    source: EncChannel | 'field-list' | undefined,
    dropIndex: number | undefined,
    useMuzeRenderer: boolean,
    zoneType: EncodingHelpers.ZoneType
  ) {
    if (channel === 'column') {
      // Add/Replace to X-Axis
      const xAxisFields = this.getSecondaryAxisFields(encoding, chartType);
      if (xAxisFields[0] !== undefined) {
        return this.replaceFieldWithFormula(encoding, xAxisFields[0], formula, channel, source, zoneType);
      } else {
        return this.addFormula(view, encoding, formula, channel, dropIndex, zoneType);
      }
    } else if (channel === 'row') {
      const leftMark = MarkType.Bar;
      const rightMark = MarkType.Line;
      // Either:
      //  - add to y
      //  - add to y2
      //  - replace y2
      // The helper functions will deal with the placement.
      if (formula.isField) {
        return EncodingHelpers.moveFieldToDualAxis(encoding, formula, channel, leftMark, rightMark, dropIndex);
      }
      const result = EncodingHelpers.addFieldAndMakeDual(encoding, formula, channel, leftMark, rightMark);
      return this.ensureDualAxis(result.encoding);
    }
  }

  /**
   * This function ensures that we have a dual axis set up and that the y-axis is a bar mark
   * and the y2-axis is a line mark.
   */
  ensureDualAxis(encoding: Encoding) {
    let result = EncodingHelpers.loadEncoding(encoding);
    if (result.encoding.y.length > 0) {
      result = EncodingHelpers.updateFieldMarkType(
        result.encoding,
        result.encoding.marks.series[0].field,
        MarkType.Bar
      );
    }
    if (result.encoding.y.length > 1) {
      result = EncodingHelpers.updateFieldMarkType(
        result.encoding,
        result.encoding.marks.series[1].field,
        MarkType.Line
      );
      result = EncodingHelpers.toggleDualAxisField(result.encoding, result.encoding.y[1].id ?? '');
    }
    return result;
  }
}
